import isNil from 'lodash/isNil'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'

const CELEBRATION_MILESTONES = [25, 50, 100, 500, 1000, 5000, 10000]

const getDismissedMilestonesKey = (userId: string): string => `omnea_dismissed_celebration_milestones_${userId}`

const getDismissedMilestones = (userId: string): number[] => {
  try {
    const stored = localStorage.getItem(getDismissedMilestonesKey(userId))
    return !isNil(stored) && stored.length > 0 ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const saveDismissedMilestone = (milestone: number, userId: string) => {
  try {
    const dismissed = getDismissedMilestones(userId)
    if (!dismissed.includes(milestone)) {
      dismissed.push(milestone)
      localStorage.setItem(getDismissedMilestonesKey(userId), JSON.stringify(dismissed))
    }
  } catch {
    // Silently fail if localStorage is not available
  }
}

const getNextMilestone = (completedCount: number, dismissedMilestones: number[]): number | null => {
  const eligibleMilestones = CELEBRATION_MILESTONES.filter(
    (milestone) => completedCount >= milestone && completedCount < milestone + 10 && !dismissedMilestones.includes(milestone)
  )

  if (eligibleMilestones.length === 0) return null

  // Return the highest milestone that hasn't been dismissed
  return eligibleMilestones[eligibleMilestones.length - 1] ?? null
}

type UseCelebrationBannerParams = {
  completedRequestCount: number
}

type UseCelebrationBannerResult = {
  shouldShowBanner: boolean
  title: string
  subtitle: string
  onDismiss: () => void
}

export const useCelebrationBanner = ({ completedRequestCount }: UseCelebrationBannerParams): UseCelebrationBannerResult => {
  const { t } = useTranslation('dashboard')
  const { REQUEST_COMPLETION_CELEBRATIONS } = useFeatureFlags()
  const { user, org } = useAuthenticatedUser()
  const [dismissedMilestones, setDismissedMilestones] = useState<number[]>(() => getDismissedMilestones(user.id))
  const [currentMilestone, setCurrentMilestone] = useState<number | null>(null)

  const isEnabled = Boolean(REQUEST_COMPLETION_CELEBRATIONS) && user.isAdmin

  useEffect(() => {
    if (!isEnabled) return

    const milestone = getNextMilestone(completedRequestCount, dismissedMilestones)
    setCurrentMilestone(milestone)
  }, [completedRequestCount, dismissedMilestones, isEnabled])

  const onDismiss = useCallback(() => {
    if (!isNil(currentMilestone)) {
      saveDismissedMilestone(currentMilestone, user.id)
      setDismissedMilestones((prev) => [...prev, currentMilestone])
      setCurrentMilestone(null)
    }
  }, [currentMilestone, user.id])

  const shouldShowBanner = isEnabled && !isNil(currentMilestone)

  const title = isNil(currentMilestone)
    ? ''
    : t('celebration-banner.title', {
        count: currentMilestone,
        defaultValue: `You've completed ${currentMilestone}+ requests in Omnea!`,
      })

  const subtitle = isNil(currentMilestone)
    ? ''
    : t('celebration-banner.subtitle', {
        orgName: org.name ?? 'your organization',
        defaultValue: `That's hours saved for everyone at ${org.name ?? 'your organization'}.`,
      })

  return {
    shouldShowBanner,
    title,
    subtitle,
    onDismiss,
  }
}
